import {
  format0,
  format1,
  format8,
  formatDecimalCurrency,
  formatWholeDollarCurrency,
  signedPercent,
} from 'utils/number';
import { fromDateParam } from 'utils/dates';
import { SLOT_CATEGORY_LABELS } from 'utils/slot-categories';
import type {
  ComparisonResult,
  EconomicAssumptions,
  EvalVersion,
  MatchStatus,
  ScenarioEconomics,
  SidePair,
} from './types';

/**
 * The Insights well page for a UWI. Shared by the Property Volume Source link, the property
 * card, and the melt table so the same well can't render as both `/wells/foo%2Fbar` and
 * `/wells/foo/bar` — the id is always URL-encoded (one convention, INS-12411 review).
 * @param {string} uwi - The well's UWI
 * @return {string} The encoded /wells/:uwi path
 */
export const wellHref = (uwi: string): string =>
  `/wells/${encodeURIComponent(uwi)}`;

/**
 * Per-slot dollar map per side (unique_slot_id => whole dollars), e.g. the Target-SIM
 * valuations — named so the shape stays in lockstep across the interface and the pricing rows.
 */
export type SlotDollarMap = {
  a: Record<string, number>;
  b: Record<string, number>;
};

/**
 * Both sides' scenario economics for a comparison, so each property row can be
 * resolved to its unit's Origin/Target assumptions. Optional at the call site:
 * when omitted, no economic rows are appended (keeps non-economics callers and
 * tests unchanged).
 */
export interface EconomicsPair {
  a: ScenarioEconomics;
  b: ScenarioEconomics;
  // Per-slot target pricing valuation for each side (unique_slot_id => whole
  // dollars: the "Target - SIM" figure), resolved per property by its own slot.
  // Optional so existing callers/tests without pricing still work.
  targetValueBySlot?: SlotDollarMap;
  // Each side's eval version. An eval-3 side sources its property OpEx/LOE from the
  // per-slot expense attributes (the area the slot resolved to) rather than the
  // unit-matched scenario economics, because the geometry-priority expense model does
  // not line up with the DPS units `units` is keyed on. Optional so existing
  // callers/tests default to the eval-2 unit-matching path.
  evalVersions?: { a: EvalVersion; b: EvalVersion };
}

export const CATEGORY_LABELS: Record<string, string> = {
  ...SLOT_CATEGORY_LABELS,
  undefined: 'Undefined',
};

export const NOT_IN_EVAL = 'Not in package eval';
export const PLACEHOLDER = '--';

/**
 * A date cell's display text: the formatted date, or the "--" placeholder when the value is
 * absent OR unparseable. `fromDateParam` returns '' (not '--') for a value that doesn't match
 * YYYY-MM-DD, which would otherwise render an empty cell and, for two differently-unparseable
 * dates, read as "not differing" (INS-12411 review). Used by every date row so a bad/foreign
 * date reads "--" consistently.
 * @param {string} v - The raw date value
 * @return {string} The formatted date or the placeholder
 */
export const dateText = (v: string): string => fromDateParam(v) || PLACEHOLDER;

/** The green/red Origin→Target delta chip for a numeric attribute. */
export interface AttrDelta {
  /** The combined chip text shown on screen, e.g. "-$83,211 (-32.5%)". */
  text: string;
  positive: boolean;
  /** The signed absolute delta alone (e.g. "-$83,211"), for the CSV Variance column. */
  abs: string;
  /**
   * The signed percent change alone (e.g. "-32.5%"), for the CSV Variance % column,
   * or null when the origin baseline is zero (no meaningful percentage).
   */
  pct: string | null;
}

/**
 * One sub-value line of a multi-value "breakdown" row (e.g. the 5-year LOE
 * series): a small tag (e.g. "Yr 1") plus the Origin/Target display values and
 * the Target-side delta. Rendered stacked, like the Interest row, with the
 * Target-side delta colored green/red by direction (as the per-property OpEx
 * rows already do; a rising LOE cost therefore reads green).
 */
export interface BreakdownLine {
  tag: string;
  aText: string;
  bText: string;
  delta: AttrDelta | null;
  /**
   * Whether the two sides diverge visibly — drives the CSV "Differ only" line filter (and,
   * via the row, the highlight). Kept in lockstep with `delta` so a line is never dropped
   * from the differences view while its rendered figures read identically (see
   * `breakdownLineDiffers`).
   */
  differs: boolean;
}

/**
 * One comparison attribute row for a property, shared by the card, table, and
 * export so all three stay in lockstep. `aText`/`bText` are display-ready
 * (already resolved to "Not in package eval" / "--"); `differs` and `delta`
 * drive the highlight and chip. The CSV export writes `aText`/`bText` as the
 * Origin/Target columns and the `delta` parts as separate Variance / Variance %
 * columns — never merged into one field.
 */
export interface AttrRow {
  key: string;
  label: string;
  aText: string;
  bText: string;
  differs: boolean;
  delta: AttrDelta | null;
  /** Optional links (Assigned Unit). */
  aHref: string | null;
  bHref: string | null;
  /**
   * Multi-value row (Property Volume, Lease Operating Expense Per Well): the
   * sub-values shown stacked in a single row with a tag; the Target-side delta is
   * colored green/red by direction. When present, the card and table render these
   * lines per side instead of the plain aText/bText.
   */
  breakdown?: BreakdownLine[];
}

/**
 * True when the given side has no slot at all for this property (the "Only in
 * Origin" / "Only in Target" case) — its cells read "Not in package eval".
 * @param {MatchStatus} matchStatus - The slot's match status
 * @param {'a' | 'b'} side - The origin (a) or target (b) side
 * @return {boolean} Whether that side is absent from the comparison
 */
export const sideMissing = (
  matchStatus: MatchStatus,
  side: 'a' | 'b'
): boolean =>
  (matchStatus === 'unmatched_b' && side === 'a') ||
  (matchStatus === 'unmatched_a' && side === 'b');

/**
 * The Origin→Target change for a numeric attribute, or null when either side is
 * absent OR the two sides display identically at this precision. `pct` is omitted
 * when the origin baseline is zero.
 *
 * The display-equality guard suppresses meaningless chips: two values that differ
 * only below the formatter's precision (e.g. an NRI that wobbles past the 8th
 * decimal) format to the same string, so there is no visible change to annotate —
 * without this they render a nonsensical "[-0.00000000 (-0.0%)]".
 * @param {number | null} a - The origin value
 * @param {number | null} b - The target value
 * @param {Function} fmt - Formatter for the absolute delta
 * @return {object | null} The delta chip ({ text, positive }) or null
 */
export const numericDelta = (
  a: number | null,
  b: number | null,
  fmt: (n: number) => string
): AttrDelta | null => {
  if (a === null || b === null) return null;
  if (fmt(a) === fmt(b)) return null;
  const diff = b - a;
  const sign = diff >= 0 ? '+' : '';
  const abs = `${sign}${fmt(diff)}`;
  // Sign the percent from its own value, not from `diff`: a negative baseline
  // flips the ratio, so a positive `diff` can still be a negative percent
  // (a = -100, b = -70 → diff +30 but −30%). Taking the sign from `diff` printed
  // "+-30.0%". `format1` already carries the minus for negatives.
  const pctChange = a !== 0 ? (diff / a) * 100 : null;
  const pct = pctChange === null ? null : signedPercent(pctChange);
  return {
    text: pct ? `${abs} (${pct})` : abs,
    positive: diff >= 0,
    abs,
    pct,
  };
};

/**
 * Whether a breakdown sub-line's two sides diverge VISIBLY, mirroring buildRow's differs: a
 * present-vs-absent side carrying a value, a matched presence mismatch, or a matched pair whose
 * `delta` is non-null. Keyed off the delta (numeric, at the line's own display precision) rather
 * than a raw !== or a formatted-string compare, so the highlight, the "Differ only" filter, and
 * the CSV stay consistent with the chip the user sees. Used by the Property Volume breakdown
 * (the LOE breakdown keeps its display-string compare).
 * @param {number | null} av - the origin value
 * @param {number | null} bv - the target value
 * @param {AttrDelta | null} delta - the line's computed Origin→Target delta, or null
 * @param {MatchStatus} matchStatus - the slot's match status
 * @return {boolean} whether the line diverges visibly
 */
const breakdownLineDiffers = (
  av: number | null,
  bv: number | null,
  delta: AttrDelta | null,
  matchStatus: MatchStatus
): boolean => {
  if (sideMissing(matchStatus, 'a')) return bv !== null;
  if (sideMissing(matchStatus, 'b')) return av !== null;
  return (av !== null) !== (bv !== null) || delta !== null;
};

// The MBOE value shown on the Property Volume "MBOE" sub-line: a BARE number (the unit lives in
// the row tag, so the cell reads "MBOE  1,234", not the doubled "MBOE  1,234 MBOE", and the CSV
// exports a sortable number rather than the text "1,234 MBOE"). A TRUE zero reads as "no volume"
// → the blank placeholder; gate on the numeric VALUE, not its formatting — format0(0.4) is '0'
// but 0.4 is real volume and must still render "0", and a non-finite input must not slip through.
export const formatMboeDisplay = (n: number): string => {
  if (!Number.isFinite(n) || n === 0) {
    return PLACEHOLDER;
  }
  return format0(n);
};
// Delta formatter for the MBOE line: one decimal (dropping a trailing .0) so a sub-1-MBOE change
// that still flags the row surfaces a visible chip, rather than a highlighted row whose whole-MBOE
// figures read identically. Bare, like the Oil/Gas line deltas — the tag carries the unit.
export const formatMboeDelta = (n: number): string =>
  format1(n).replace(/\.0$/, '');
export const formatCategory = (v: string): string => CATEGORY_LABELS[v] ?? v;
// Well status → Title Case (e.g. "PRODUCING"/"producing" → "Producing").
export const formatStatus = (v: string): string =>
  v
    .replace(/_/g, ' ')
    .replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

// Resolves a side's display text, honoring the "missing side" case.
const sideText = <T>(
  value: T | null,
  side: 'a' | 'b',
  matchStatus: MatchStatus,
  format: (v: T) => string
): string => {
  if (sideMissing(matchStatus, side)) return NOT_IN_EVAL;
  if (value === null || value === undefined || value === '') return PLACEHOLDER;
  return format(value);
};

interface RowSpec<T> {
  key: string;
  label: string;
  pair: SidePair<T>;
  format: (v: T) => string;
  delta?: AttrDelta | null;
  href?: (v: T) => string | null;
}

// Builds one AttrRow from a typed spec, resolving both sides' display text, the
// differs flag, and the export string.
const buildRow = <T>(spec: RowSpec<T>, matchStatus: MatchStatus): AttrRow => {
  const { key, label, pair, format, delta = null, href } = spec;
  const aText = sideText(pair.a, 'a', matchStatus, format);
  const bText = sideText(pair.b, 'b', matchStatus, format);
  const aMissing = sideMissing(matchStatus, 'a');
  const bMissing = sideMissing(matchStatus, 'b');
  const hasValue = (v: T | null): boolean =>
    v !== null && v !== undefined && (v as unknown) !== '';
  // A row "differs" when the two sides diverge: one side absent from the comparison
  // (Only in Origin / Only in Target) with a value on the present side; OR, for a
  // matched property, a presence mismatch (one value null/blank, the other set) OR
  // two present values whose DISPLAYED text differs. Comparing display text (not the
  // raw values) means a sub-precision-only divergence — e.g. NRI values that differ
  // below the 8th decimal but render identically — is not a difference, so it neither
  // highlights the cell nor shows a "[-0.00000000 (-0.0%)]" chip (matching numericDelta).
  // Presence is compared independently so a { a: null, b: '2026-01-01' } pair still
  // counts as a difference (drives the default differences-only view, the Difference
  // filter, and the `differ` CSV).
  const differs =
    aMissing || bMissing
      ? hasValue(aMissing ? pair.b : pair.a)
      : hasValue(pair.a) !== hasValue(pair.b) ||
        (hasValue(pair.a) && hasValue(pair.b) && aText !== bText);
  return {
    key,
    label,
    aText,
    bText,
    differs,
    delta,
    aHref:
      href && pair.a !== null && pair.a !== undefined ? href(pair.a) : null,
    bHref:
      href && pair.b !== null && pair.b !== undefined ? href(pair.b) : null,
  };
};

/**
 * A stacked multi-value "breakdown" row (like LOE / Property Volume): the joined aText/bText are
 * a flat single-cell fallback, while the card, table, and CSV consume `breakdown`. The row-level
 * delta stays null — each sub-line carries its own — and it never links.
 * @param {string} key - The row key
 * @param {string} label - The row label
 * @param {BreakdownLine[]} lines - The stacked sub-value lines
 * @return {AttrRow} The breakdown row
 */
const makeBreakdownRow = (
  key: string,
  label: string,
  lines: BreakdownLine[]
): AttrRow => ({
  key,
  label,
  aText: lines.map((l) => `${l.tag} ${l.aText}`).join(' · '),
  bText: lines.map((l) => `${l.tag} ${l.bText}`).join(' · '),
  differs: lines.some((l) => l.differs),
  delta: null,
  aHref: null,
  bHref: null,
  breakdown: lines,
});

// The AFE cost buckets in display order (INS-12411): the ComparisonAttributes cost key, its
// cost-date key, and the tag shown on the stacked AFE Costs / AFE Cost Dates breakdown rows.
const AFE_COST_BUCKETS = [
  { costKey: 'afeDrillingCost', dateKey: 'afeDrillingDate', tag: 'Drilling' },
  {
    costKey: 'afeCompletionCost',
    dateKey: 'afeCompletionDate',
    tag: 'Completion',
  },
  {
    costKey: 'afeFacilitiesCost',
    dateKey: 'afeFacilitiesDate',
    tag: 'Facilities',
  },
  {
    costKey: 'afeContingenciesCost',
    dateKey: 'afeContingenciesDate',
    tag: 'Contingencies',
  },
  { costKey: 'afeOtherCost', dateKey: 'afeOtherDate', tag: 'Other' },
] as const;

// The 5-year LOE-per-well series, in display order (tag + the loePerWell key).
const LOE_ECONOMIC_YEARS = [
  { key: 'year1', tag: 'Yr 1' },
  { key: 'year2', tag: 'Yr 2' },
  { key: 'year3', tag: 'Yr 3' },
  { key: 'year4', tag: 'Yr 4' },
  { key: 'year5AndUp', tag: 'Yr 5+' },
] as const;

/**
 * A property's effective economics for one side: the assumptions of the unit
 * (development area) it belongs to, or the scenario-global assumptions when that
 * unit isn't configured. Null when the side is absent (the property isn't in that
 * package eval), so its cells fall back to "Not in package eval".
 * @param {ScenarioEconomics} economics - One side's scenario economics
 * @param {string | null} unitName - The property's development area on that side
 * @return {EconomicAssumptions | null} The resolved assumptions, or null
 */
const resolveUnitEconomics = (
  economics: ScenarioEconomics,
  unitName: string | null
): EconomicAssumptions | null => {
  if (!unitName) return null;
  const unit = economics.units.find((u) => u.developmentAreaName === unitName);
  return unit ?? economics;
};

/** A LOE-per-well series with every year blank — the eval-3 fallback when a slot
 *  carries no LOE (present-and-null), so the breakdown still renders "--" rows. */
const EMPTY_LOE_PER_WELL: EconomicAssumptions['loePerWell'] = {
  year1: null,
  year2: null,
  year3: null,
  year4: null,
  year5AndUp: null,
};

/**
 * One side's effective economics for a property. An eval-3 side reads the per-slot
 * expense rates the BE stamped on this property (the OpEx/LOE of the area the slot
 * resolved to), because the geometry-priority expense model does not line up with the
 * DPS units the scenario economics is keyed on. An eval-2 side keeps resolving to its
 * unit's assumptions. An eval-3 absent side reads present-and-null and lets matchStatus
 * render "Not in package eval" — the same behavior the AFE rows already rely on.
 * @param {ComparisonResult} result - The property's comparison result
 * @param {'a' | 'b'} side - Which side to resolve
 * @param {EconomicsPair} economics - Both sides' economics, plus their eval versions
 * @return {EconomicAssumptions | null} The side's assumptions, or null (eval-2 absent side)
 */
const sideEconomics = (
  result: ComparisonResult,
  side: 'a' | 'b',
  economics: EconomicsPair
): EconomicAssumptions | null => {
  const { attributes } = result;
  // An eval-3 side reads the per-slot expense rates the BE stamped on THIS property, but only for a
  // slot that was actually stamped. apply_expense_rates! stamps loePerWell (a non-null hash) on
  // every stamped slot — even when an individual OpEx is null — and leaves an un-stamped slot
  // without it (null) when the sidecar did not key-match it: a non-tract `nta_` slot, a slot keyed
  // by its UWI the sidecar missed, or an area deleted since the run. loePerWell !== null is thus the
  // EXACT stamp signal — a stamped-but-partial slot (e.g. no oil OpEx) still reads its own "--" here
  // rather than borrowing the side's unit economics, and a side with no expense domain has no
  // stamped slot so every row falls back. No side-level flag is needed: the per-slot signal already
  // tells a stamped side from an un-stamped one (INS-12510 review).
  const stamped = attributes.loePerWell[side] !== null;
  if (economics.evalVersions?.[side] === 'eval_3' && stamped) {
    return {
      oilOpexCost: attributes.oilOpexCost[side],
      gasOpexCost: attributes.gasOpexCost[side],
      waterOpexCost: attributes.waterOpexCost[side],
      loePerWell: attributes.loePerWell[side] ?? EMPTY_LOE_PER_WELL,
    };
  }
  return resolveUnitEconomics(economics[side], attributes.assignedUnit[side]);
};

/**
 * The per-property economic rows: Oil/Gas/Water OpEx as plain rows, then a single
 * "Lease Operating Expense Per Well" row whose 5-year series
 * is a stacked, uncolored breakdown (like Interest). Each side resolves to the
 * property's unit; buildRow/sideText give them the shared differs / delta /
 * "Not in package eval" behavior.
 * @param {ComparisonResult} result - The property's comparison result
 * @param {EconomicsPair} economics - Both sides' scenario economics
 * @return {AttrRow[]} The economic attribute rows
 */
const buildEconomicRows = (
  result: ComparisonResult,
  economics: EconomicsPair
): AttrRow[] => {
  const { matchStatus } = result;
  const a = sideEconomics(result, 'a', economics);
  const b = sideEconomics(result, 'b', economics);

  const opexRow = (
    key: string,
    label: string,
    pick: (e: EconomicAssumptions) => number | null
  ): AttrRow => {
    const av = a ? pick(a) : null;
    const bv = b ? pick(b) : null;
    return buildRow(
      {
        key,
        label,
        pair: { a: av, b: bv },
        format: formatDecimalCurrency,
        delta: numericDelta(av, bv, formatDecimalCurrency),
      },
      matchStatus
    );
  };

  const loeLines: BreakdownLine[] = LOE_ECONOMIC_YEARS.map(({ key, tag }) => {
    const av = a ? a.loePerWell[key] : null;
    const bv = b ? b.loePerWell[key] : null;
    const aText = sideText(av, 'a', matchStatus, formatWholeDollarCurrency);
    const bText = sideText(bv, 'b', matchStatus, formatWholeDollarCurrency);
    return {
      tag,
      aText,
      bText,
      delta: numericDelta(av, bv, formatWholeDollarCurrency),
      // LOE keeps its original display-string differ (Origin vs Target text differ), so a
      // one-sided property still surfaces its "Not in package eval" vs "--" LOE row. The
      // numeric line-differ (breakdownLineDiffers) is used only by the Property Volume row,
      // which needs a delta-precision compare to reveal sub-display MBOE changes.
      differs: aText !== bText,
    };
  });
  // The card, table, and CSV all consume the stacked `breakdown` — the CSV melts it into one
  // row per year, each with its own Variance / Variance %. The joined aText/bText remain only
  // as a flat single-cell fallback; `differs` is computed from the per-year lines.
  const loeRow = makeBreakdownRow(
    'loePerWell',
    'Lease Operating Expense Per Well',
    loeLines
  );

  // Per-slot pricing valuations (whole dollars), resolved to the property's OWN slot
  // (unique_slot_id) so each card shows the exact "Target - SIM" figure that slot shows
  // on its eval page — Origin vs Target with a signed delta.
  const slotDollar = (
    map: SlotDollarMap | undefined,
    side: 'a' | 'b'
  ): number | null => {
    const slotId = result.uniqueSlotId;
    if (!map || !slotId) return null;
    return map[side][slotId] ?? null;
  };

  const pricingRow = (
    key: string,
    label: string,
    map: SlotDollarMap | undefined
  ): AttrRow => {
    const av = slotDollar(map, 'a');
    const bv = slotDollar(map, 'b');
    return buildRow(
      {
        key,
        label,
        pair: { a: av, b: bv },
        format: formatWholeDollarCurrency,
        delta: numericDelta(av, bv, formatWholeDollarCurrency),
      },
      matchStatus
    );
  };

  return [
    opexRow('oilOpex', 'Oil OpEx ($/bbl)', (e) => e.oilOpexCost),
    opexRow('gasOpex', 'Gas OpEx ($/mcf)', (e) => e.gasOpexCost),
    opexRow('waterOpex', 'Water OpEx ($/bbl)', (e) => e.waterOpexCost),
    loeRow,
    pricingRow(
      'targetValue',
      'Total Target Value',
      economics.targetValueBySlot
    ),
  ];
};

/**
 * The ordered comparison attribute rows for a property, matching mock v16's card
 * and table: NRI, WI, Property Volume Source, Property Volume, Property Res Cat,
 * Assigned Unit, and the Permit/Spud/Completion/First-Production dates. When
 * `economics` is supplied, the property's unit-resolved Oil/Gas/Water OpEx and
 * Lease Operating Expense Per Well (5-year breakdown) rows are appended.
 *
 * NRI and WI are two separate plain rows (8-decimal), each with an absolute + %
 * delta on the Target side when it changes — like the other numeric attributes.
 * @param {ComparisonResult} result - The property's comparison result
 * @param {EconomicsPair | null} economics - Both sides' scenario economics, or null
 * @return {AttrRow[]} The ordered attribute rows
 */
export const buildAttributeRows = (
  result: ComparisonResult,
  economics: EconomicsPair | null = null
): AttrRow[] => {
  const { matchStatus, comparisons, attributes } = result;
  const nriRow = buildRow(
    {
      key: 'nri',
      label: 'NRI',
      pair: { a: comparisons.nri.valueA, b: comparisons.nri.valueB },
      format: format8,
      delta: numericDelta(
        comparisons.nri.valueA,
        comparisons.nri.valueB,
        format8
      ),
    },
    matchStatus
  );
  const wiRow = buildRow(
    {
      key: 'wi',
      label: 'WI',
      pair: { a: comparisons.wi.valueA, b: comparisons.wi.valueB },
      format: format8,
      delta: numericDelta(
        comparisons.wi.valueA,
        comparisons.wi.valueB,
        format8
      ),
    },
    matchStatus
  );

  // Property Volume Source links to the associated Insights page (INS-12411). Both the
  // type-curve detail href and the pdp-decline /wells/:uwi link are now BE-served
  // (Comparison#volume_source_href_for at request time), so the FE passes the href through for
  // both — never constructing a route here (HATEOAS convention). Other/absent sources stay plain
  // text; a missing side has null type, so it never links (and reads "Not in package eval").
  const volumeSourceLink = (side: 'a' | 'b'): string | null => {
    const type = attributes.volumeSourceType[side];
    if (type === 'type_curve' || type === 'pdp_decline') {
      return attributes.volumeSourceHref[side];
    }
    return null;
  };
  const volumeSourceRow: AttrRow = {
    ...buildRow(
      {
        key: 'volumeSource',
        label: 'Property Volume Source',
        // A DCA forecast shows its name, plain text; the id is the fallback.
        pair: {
          a: attributes.volumeSourceName?.a ?? attributes.volumeSource.a,
          b: attributes.volumeSourceName?.b ?? attributes.volumeSource.b,
        },
        format: (v) => v,
      },
      matchStatus
    ),
    aHref: volumeSourceLink('a'),
    bHref: volumeSourceLink('b'),
  };

  // Property Volume groups the slot's type curve per-well EUR as stacked sub-lines in ONE
  // row — an MBOE roll-up plus Oil (Mbbl) and Gas (MMcf). The BE scales the curve's
  // reference-lateral EUR to each slot's actual lateral (apply_type_curve_eur!) and rolls the
  // MBOE up itself (attributes.mboe, via the single Comparison.eur_to_mboe divisor), so these
  // reflect what the eval recovers for that well — NOT the raw type-curve page number — and the
  // card MBOE can't drift from the summary Total Volume. EUR is present only for type-curve
  // slots; blank otherwise. Mirrors the LOE breakdown row.
  const eurLine = (tag: string, pair: SidePair<number>): BreakdownLine => {
    const delta = numericDelta(pair.a, pair.b, format0);
    return {
      tag,
      aText: sideText(pair.a, 'a', matchStatus, format0),
      bText: sideText(pair.b, 'b', matchStatus, format0),
      delta,
      differs: breakdownLineDiffers(pair.a, pair.b, delta, matchStatus),
    };
  };
  const mboeDelta = numericDelta(
    attributes.mboe.a,
    attributes.mboe.b,
    formatMboeDelta
  );
  const volumeLines: BreakdownLine[] = [
    {
      tag: 'MBOE',
      aText: sideText(attributes.mboe.a, 'a', matchStatus, formatMboeDisplay),
      bText: sideText(attributes.mboe.b, 'b', matchStatus, formatMboeDisplay),
      delta: mboeDelta,
      differs: breakdownLineDiffers(
        attributes.mboe.a,
        attributes.mboe.b,
        mboeDelta,
        matchStatus
      ),
    },
    eurLine('Oil EUR (Mbbl)', attributes.oilEur),
    eurLine('Gas EUR (MMcf)', attributes.gasEur),
  ];
  // The row differs when any sub-line does (breakdownLineDiffers) — a numeric compare at each
  // line's display precision — so the highlight stays in lockstep with the chips and the CSV
  // "Differ only" filter (no highlight without a visible change); the MBOE line's delta reveals
  // a sub-1-MBOE move (formatMboeDelta). The row-level delta stays null by design, as on every
  // breakdown row: each sub-line carries its own chip (BreakdownCell), so a row-level value
  // (CardCell reads row.delta only for plain rows) would never render.
  const volumeRow = makeBreakdownRow('volume', 'Property Volume', volumeLines);

  // AFE rows (INS-12411): AFE ID (linked to its Insights catalog page via the BE-provided href),
  // AFE Name, and the AFE Costs / AFE Cost Dates as stacked breakdown rows (the 5 buckets, plus a
  // cost total), mirroring the eval-3 Slot Valuation Inputs tearsheet. Costs format as 2-decimal
  // dollars, dates as MM/DD/YYYY; a property with no AFE reads "--".
  const afeIdRow: AttrRow = {
    ...buildRow(
      {
        key: 'afeId',
        label: 'AFE ID',
        pair: attributes.afeId,
        format: (v) => v,
      },
      matchStatus
    ),
    aHref: attributes.afeHref.a,
    bHref: attributes.afeHref.b,
  };
  const afeNameRow = buildRow(
    {
      key: 'afeName',
      label: 'AFE Name',
      pair: attributes.afeName,
      format: (v) => v,
    },
    matchStatus
  );
  const afeCostLine = (tag: string, pair: SidePair<number>): BreakdownLine => {
    const aText = sideText(pair.a, 'a', matchStatus, formatDecimalCurrency);
    const bText = sideText(pair.b, 'b', matchStatus, formatDecimalCurrency);
    return {
      tag,
      aText,
      bText,
      delta: numericDelta(pair.a, pair.b, formatDecimalCurrency),
      differs: aText !== bText,
    };
  };
  const afeDateLine = (tag: string, pair: SidePair<string>): BreakdownLine => {
    const aText = sideText(pair.a, 'a', matchStatus, dateText);
    const bText = sideText(pair.b, 'b', matchStatus, dateText);
    // Dates carry no numeric delta — only a present/absent-or-changed differ.
    return { tag, aText, bText, delta: null, differs: aText !== bText };
  };
  const afeCostsRow = makeBreakdownRow('afeCosts', 'AFE Costs', [
    ...AFE_COST_BUCKETS.map(({ costKey, tag }) =>
      afeCostLine(tag, attributes[costKey])
    ),
    afeCostLine('Total', attributes.afeTotalCost),
  ]);
  const afeCostDatesRow = makeBreakdownRow(
    'afeCostDates',
    'AFE Cost Dates',
    AFE_COST_BUCKETS.map(({ dateKey, tag }) =>
      afeDateLine(tag, attributes[dateKey])
    )
  );

  const rows: AttrRow[] = [
    nriRow,
    wiRow,
    volumeSourceRow,
    volumeRow,
    buildRow(
      {
        key: 'resCategory',
        label: 'Property Res Cat',
        pair: attributes.resCategory,
        format: formatCategory,
      },
      matchStatus
    ),
    buildRow(
      {
        key: 'status',
        label: 'Status',
        pair: attributes.status,
        format: formatStatus,
      },
      matchStatus
    ),
    buildRow(
      {
        key: 'assignedUnit',
        label: 'Assigned Unit',
        pair: attributes.assignedUnit,
        format: (v) => v,
        href: (v) => `/development_areas/${encodeURIComponent(v)}`,
      },
      matchStatus
    ),
    buildRow(
      {
        key: 'permitDate',
        label: 'Permit Date',
        pair: attributes.permitDate,
        format: dateText,
      },
      matchStatus
    ),
    buildRow(
      {
        key: 'spudDate',
        label: 'Spud Date',
        pair: attributes.spudDate,
        format: dateText,
      },
      matchStatus
    ),
    buildRow(
      {
        key: 'completionDate',
        label: 'Completion Date',
        pair: attributes.completionDate,
        format: dateText,
      },
      matchStatus
    ),
    buildRow(
      {
        key: 'firstProduction',
        label: 'First Production',
        pair: attributes.firstProductionDate,
        format: dateText,
      },
      matchStatus
    ),
    afeIdRow,
    afeNameRow,
    afeCostsRow,
    afeCostDatesRow,
  ];

  if (economics) rows.push(...buildEconomicRows(result, economics));
  return rows;
};
